import * as yup from 'yup';
import { SupplierDeliveryType } from 'types';
import { useDispatch } from 'react-redux';
import { FieldValues } from 'react-hook-form';
import { FormModal } from 'components/form/FormModal/FormModal';
import { activateSnackbar } from 'store/features/ui';
import { FormCheckbox, FormInputGroup, FormText } from 'components/form';
import { addOrUpdateSupplierDeliveryType } from 'lib/firebase';
import { DefaultOpenModalProps } from 'components/button/OpenModalButton/OpenModalButton';

const defaultAddDeliveryTypeValues: SupplierDeliveryType = {
    deliveryTypeName: '',
    willCall: false,
};

export interface AddDeliveryTypeModalProps extends DefaultOpenModalProps {
    supplierId?: string;
    onAddDeliveryType?: (newDeliveryType: SupplierDeliveryType) => void;
    deliveryTypes?: SupplierDeliveryType[];
}

export const AddDeliveryTypeModal = ({
    modalOpen,
    setModalOpen,
    supplierId,
    onAddDeliveryType,
    deliveryTypes,
}: AddDeliveryTypeModalProps) => {
    const dispatch = useDispatch();

    const nameIsUnique = (
        newName: string,
        deliveryTypes: SupplierDeliveryType[]
    ): boolean => {
        return (
            deliveryTypes.filter(
                (deliveryType: SupplierDeliveryType) =>
                    deliveryType.deliveryTypeName === newName
            ).length === 0
        );
    };

    const addDeliveryTypeSchema = yup.object<{
        [k in keyof SupplierDeliveryType]: yup.AnySchema;
    }>({
        deliveryTypeName: yup
            .string()
            .required()
            .test(
                'Duplicate',
                'A delivery type with this name already exists.',
                (values) => {
                    if (values && deliveryTypes)
                        return nameIsUnique(values, deliveryTypes);
                    else return true;
                }
            ),
        willCall: yup.boolean().required(),
    });

    const onError = () => {
        dispatch(
            activateSnackbar({
                message: 'There was a problem creating a new delivery type.',
                severity: 'error',
            })
        );
    };

    const onSubmit = (data: FieldValues) => {
        if (supplierId && onAddDeliveryType) {
            addOrUpdateSupplierDeliveryType(
                supplierId,
                data.deliveryTypeName,
                data.willCall
            ).then(() => {
                setModalOpen(false);
                const newDeliveryType: SupplierDeliveryType = {
                    deliveryTypeName: data.deliveryTypeName,
                    willCall: data.willCall,
                };
                onAddDeliveryType(newDeliveryType);
            });
        }
    };

    return (
        <FormModal
            validationSchema={addDeliveryTypeSchema}
            defaultValues={defaultAddDeliveryTypeValues}
            title="Add New Delivery Type"
            modalOpen={modalOpen}
            setModalOpen={setModalOpen}
            onValidHandler={onSubmit}
            onErrorHandler={onError}
            testId="AddDeliveryTypeModal"
            maxWidth="xs"
        >
            <FormInputGroup>
                <FormText field="deliveryTypeName" label="Delivery Type Name" />
            </FormInputGroup>
            <FormInputGroup>
                <FormCheckbox field="willCall" label="Will Call" />
            </FormInputGroup>
        </FormModal>
    );
};
